// deps
import React, { ReactElement, memo } from "react";
import cx from "classnames";

//internal-deps
import { ScreenReaderOnly } from "@swiggy-private/core-ui";

// styles
import styles from "./DeliveryAddressView.scss";

// constants
import { ACCESSIBILITY_TEXTS } from "./constants";

// test-ids
import { DELIVERY_ADDRESS_VIEW_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface DeliveryAddressViewProps {
    pickupName: string;
    pickupSubtext: string;
    PickupIcon: React.FunctionComponent;
    LineIcon: React.FunctionComponent;
    deliveryAddress: string;
    deliveryAnnotation: string;
    DeliveryIcon: React.FunctionComponent;
    couponBannerVisible: boolean;
    deliveryTimeAnnotation?: string;
    deliveryTime?: string;
}

function DeliveryAddressView(props: DeliveryAddressViewProps): ReactElement | null {
    const {
        PickupIcon,
        pickupName,
        deliveryAddress,
        deliveryAnnotation,
        DeliveryIcon,
        couponBannerVisible,
        pickupSubtext,
        deliveryTimeAnnotation,
        deliveryTime,
        LineIcon,
    } = props;
    const wrapperStyles = cx(styles.wrapper, { [styles.wrapperWithoutCoupon]: !couponBannerVisible });
    return (
        <>
            <div className={wrapperStyles}>
                <div className={styles.innerWrapper}>
                    <div className={styles.icons}>
                        <span data-testid={DELIVERY_ADDRESS_VIEW_TEST_IDS.OUTLET_ICON}>
                            <PickupIcon />
                        </span>
                        <LineIcon />
                        <span data-testid={DELIVERY_ADDRESS_VIEW_TEST_IDS.CUSTOMER_ADDRESS_ICON}>
                            <DeliveryIcon />
                        </span>
                    </div>
                    <ScreenReaderOnly
                        text={`${ACCESSIBILITY_TEXTS.DELIVERING_TO} ${deliveryAnnotation} ${deliveryAddress}`}
                    />
                    <div className={styles.details} aria-hidden="true">
                        <div className={styles.pickupDetails}>
                            <div
                                className={styles.restaurantName}
                                data-testid={DELIVERY_ADDRESS_VIEW_TEST_IDS.OUTLET_NAME}
                            >
                                {pickupName}
                            </div>

                            {pickupSubtext && (
                                <>
                                    <span className={styles.separator}>|</span>
                                    <p className={styles.pickupSubtext}>{pickupSubtext}</p>
                                </>
                            )}
                        </div>

                        <div className={styles.deliveryDetails}>
                            <div
                                className={styles.annotation}
                                data-testid={DELIVERY_ADDRESS_VIEW_TEST_IDS.ADDRESS_ANNOTATION}
                            >
                                {deliveryAnnotation}
                            </div>
                            {deliveryAddress && (
                                <>
                                    <span className={styles.separator}>|</span>
                                    <div
                                        className={styles.deliveryAddress}
                                        data-testid={DELIVERY_ADDRESS_VIEW_TEST_IDS.ADDRESS_DETAILS}
                                    >
                                        {deliveryAddress}
                                    </div>
                                </>
                            )}
                        </div>
                        {deliveryTimeAnnotation && deliveryTime && (
                            <div className={styles.deliveryTimeContainer}>
                                <span className={styles.deliveryTimeAnnotation}>{deliveryTimeAnnotation}</span>
                                <span className={styles.deliveryTimeText}>{deliveryTime}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default memo(DeliveryAddressView);
